import React, { useEffect, useRef, useState } from 'react';
import api from '../api/axiosConfig';
import { refreshPremium } from '../hooks/usePremium';
import { FALLBACK_PRICING, Pricing, formatMoney, monthlyEquivalentMoney } from '../hooks/usePricing';
import { useLanguage } from '../i18n/LanguageContext';
import './CheckoutModal.css';

// Subscription checkout via ONVO's drop-in card widget: the backend
// creates an incomplete subscription, the widget collects the card and
// confirms the first charge, then we verify server-side (never trusting
// the browser) before treating the account as premium.

interface CheckoutModalProps {
    plan: 'monthly' | 'yearly';
    onClose: () => void;
    onSuccess: () => void;
}

const ONVO_SDK_URL = 'https://sdk.onvopay.com/sdk.js';

// The failure message is passed in so it can be translated; the promise is
// memoised, so it is the first caller's language that names a failure.
let onvoScriptPromise: Promise<void> | null = null;
const loadOnvoSdk = (failureMessage: string): Promise<void> => {
    if ((window as any).onvo) return Promise.resolve();
    if (onvoScriptPromise) return onvoScriptPromise;
    onvoScriptPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = ONVO_SDK_URL;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error(failureMessage));
        document.head.appendChild(s);
    });
    return onvoScriptPromise;
};

export const CheckoutModal: React.FC<CheckoutModalProps> = ({ plan, onClose, onSuccess }) => {
    const { t, language } = useLanguage();
    const containerRef = useRef<HTMLDivElement>(null);
    const [phase, setPhase] = useState<'loading' | 'card' | 'verifying' | 'done'>('loading');
    const [error, setError] = useState<string | null>(null);
    // The subscribe response carries the band the backend actually charged,
    // so the price shown here is the price on the subscription -- not a
    // guess the page made before the band was resolved.
    const [pricing, setPricing] = useState<Pricing>(FALLBACK_PRICING);
    const planLabel = plan === 'monthly' ? t.billing.checkoutPlanMonthly : t.billing.checkoutPlanYearly;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.post('/billing/subscribe/', { plan });
                if (!cancelled && data.pricing) setPricing(data.pricing);
                await loadOnvoSdk(t.billing.paymentFormLoadFailed);
                if (cancelled || !containerRef.current) return;

                const onvo = (window as any).onvo;
                onvo.pay({
                    publicKey: data.public_key,
                    subscriptionId: data.subscription_id,
                    customerId: data.customer_id,
                    paymentType: 'subscription',
                    locale: 'en',
                    onSuccess: async () => {
                        setPhase('verifying');
                        try {
                            const res = await api.post('/billing/confirm/', {});
                            if (res.data.premium) {
                                refreshPremium();  // unlock gated UI app-wide
                                setPhase('done');
                                onSuccess();
                            } else {
                                setError(t.billing.paymentNotConfirmed);
                            }
                        } catch {
                            setError(t.billing.confirmationFailed);
                        }
                    },
                    onError: (e: any) => {
                        setError(e?.message || t.billing.paymentFailed);
                        setPhase('card');
                    },
                }).render('#onvo-checkout-container');
                setPhase('card');
            } catch (e: any) {
                if (!cancelled) {
                    setError(e?.response?.data?.error || t.billing.checkoutStartFailed);
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan]);

    return (
        <div className="checkout-overlay" onClick={() => phase !== 'verifying' && onClose()}>
            <div className="checkout-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="checkout-header">
                    <h3>{t.billing.upgradeHeading.replace('{plan}', planLabel)}</h3>
                    <button className="checkout-close" onClick={onClose} disabled={phase === 'verifying'}>✕</button>
                </div>
                <p className="checkout-price">
                    {plan === 'monthly'
                        ? t.billing.priceMonthly.replace('{price}', formatMoney(pricing.monthly, pricing.currency, language))
                        : t.billing.priceYearly
                            .replace('{price}', formatMoney(pricing.yearly, pricing.currency, language))
                            .replace('{monthly}', monthlyEquivalentMoney(pricing, language))}
                </p>

                {error && <div className="checkout-error">{error}</div>}

                {phase === 'loading' && <div className="checkout-loading">{t.billing.preparingCheckout}</div>}
                {phase === 'verifying' && <div className="checkout-loading">{t.billing.confirmingSubscription}</div>}
                {phase === 'done' && (
                    <div className="checkout-success">
                        🎉 {t.billing.trialStarted}
                    </div>
                )}

                <div
                    id="onvo-checkout-container"
                    ref={containerRef}
                    style={{ display: phase === 'card' ? 'block' : 'none' }}
                />

                {phase === 'done' && (
                    <button className="checkout-done-btn" onClick={onClose}>{t.billing.done}</button>
                )}
            </div>
        </div>
    );
};

export default CheckoutModal;
